/**
 * AIS Spoofing & Disruption Detection — flags anomalous vessel behavior.
 *
 * Detects four categories of AIS anomaly:
 * 1. Impossible speed: reported SOG > 50 knots (most ships max ~25kts)
 * 2. Position jump: vessel moves >100 nautical miles in <5 minutes
 * 3. Dark zone: vessel position reports stop in a known-sensitive area
 * 4. Dark ship: vessel goes silent (>30 min) in any major shipping lane
 *
 * Maintains a rolling position history per MMSI to compare consecutive
 * reports. History is pruned after 1 hour to bound memory.
 */

import type { NormalizedEvent } from '@/sources/base-source';
import { getEventTimestamp } from './types';

export interface AISAnomaly {
  mmsi: number;
  vesselName: string;
  anomalyType: 'impossible-speed' | 'position-jump' | 'dark-zone' | 'dark-ship';
  description: string;
  lat: number;
  lng: number;
  detectedAt: string;
  /** For speed anomalies: the reported speed in knots. */
  reportedSpeed?: number;
  /** For position jumps: distance in nautical miles. */
  jumpDistanceNm?: number;
  /** For position jumps: time delta in seconds. */
  timeDeltaSec?: number;
  /** For dark zones: seconds since last report. */
  silenceDurationSec?: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum plausible speed in knots (50 kts — high-speed military vessels). */
const MAX_SPEED_KTS = 50;

/** Maximum plausible distance (nm) in a 5-minute window before flagging. */
const MAX_DISTANCE_NM_5MIN = 100;

/** Five minutes in milliseconds. */
const FIVE_MINUTES_MS = 5 * 60 * 1000;

/** Minimum silence (seconds) in a dark zone to flag vessel as gone-dark. */
const DARK_ZONE_SILENCE_SEC = 1800; // 30 minutes

/** Position history TTL — drop entries older than 1 hour. */
const HISTORY_TTL_MS = 60 * 60 * 1000;

/** Maximum tracked vessels to prevent unbounded memory growth. */
const MAX_TRACKED_VESSELS = 10_000;

// ---------------------------------------------------------------------------
// Dark zones — known areas where AIS disruption is suspicious
// ---------------------------------------------------------------------------

interface DarkZone {
  name: string;
  lat: number;
  lng: number;
  radiusNm: number;
}

const DARK_ZONES: DarkZone[] = [
  { name: 'Strait of Hormuz', lat: 26.5, lng: 56.3, radiusNm: 60 },
  { name: 'Bab el-Mandeb', lat: 12.6, lng: 43.3, radiusNm: 40 },
  { name: 'Strait of Malacca', lat: 2.5, lng: 101.5, radiusNm: 80 },
  { name: 'South China Sea (Spratly)', lat: 10.0, lng: 114.0, radiusNm: 120 },
  { name: 'Black Sea (Western)', lat: 43.5, lng: 31.0, radiusNm: 100 },
  { name: 'Eastern Mediterranean', lat: 34.5, lng: 33.5, radiusNm: 80 },
  { name: 'Gulf of Guinea', lat: 3.0, lng: 3.0, radiusNm: 100 },
  { name: 'Persian Gulf', lat: 27.0, lng: 51.0, radiusNm: 80 },
  { name: 'Taiwan Strait', lat: 24.5, lng: 119.5, radiusNm: 60 },
  { name: 'Sea of Azov', lat: 46.0, lng: 36.5, radiusNm: 50 },
];

// ---------------------------------------------------------------------------
// Shipping lanes — broader coverage for dark-ship detection
// ---------------------------------------------------------------------------

interface ShippingLane {
  name: string;
  lat: number;
  lng: number;
  radiusNm: number;
}

/**
 * Major global shipping lane regions. Vessels going dark in these areas
 * are flagged as potential dark ships (AIS transponder intentionally disabled).
 * These are broader than DARK_ZONES which focus on geopolitically sensitive areas.
 */
const SHIPPING_LANES: ShippingLane[] = [
  // Europe
  { name: 'English Channel', lat: 50.5, lng: 1.0, radiusNm: 60 },
  { name: 'North Sea', lat: 56.0, lng: 3.5, radiusNm: 150 },
  { name: 'Baltic Approaches', lat: 56.0, lng: 11.5, radiusNm: 80 },
  { name: 'Gibraltar Strait', lat: 36.0, lng: -5.5, radiusNm: 40 },
  // Africa
  { name: 'Suez Canal Approaches', lat: 30.0, lng: 32.5, radiusNm: 50 },
  { name: 'Cape of Good Hope', lat: -34.5, lng: 18.5, radiusNm: 80 },
  { name: 'Mozambique Channel', lat: -15.0, lng: 42.0, radiusNm: 120 },
  // Asia
  { name: 'Singapore Strait', lat: 1.2, lng: 103.8, radiusNm: 40 },
  { name: 'Lombok Strait', lat: -8.5, lng: 115.7, radiusNm: 40 },
  { name: 'Korea Strait', lat: 34.0, lng: 129.0, radiusNm: 60 },
  { name: 'East China Sea', lat: 30.0, lng: 125.0, radiusNm: 100 },
  // Americas
  { name: 'Panama Canal Approaches', lat: 9.0, lng: -79.5, radiusNm: 50 },
  { name: 'Gulf of Mexico', lat: 27.0, lng: -90.0, radiusNm: 150 },
  { name: 'US East Coast', lat: 37.0, lng: -74.0, radiusNm: 100 },
];

/** Track vessels already flagged for dark-ship silence (prevents duplicate alerts). */
const darkShipFlagged: Set<number> = new Set();

// ---------------------------------------------------------------------------
// Position history
// ---------------------------------------------------------------------------

interface VesselPosition {
  lat: number;
  lng: number;
  timestampMs: number;
  speed: number;
  name: string;
}

/** Per-vessel most-recent position for comparison. */
const vesselHistory: Map<number, VesselPosition> = new Map();

/** Track vessels already flagged for dark-zone silence (prevents duplicate alerts). */
const darkZoneFlagged: Set<number> = new Set();

// ---------------------------------------------------------------------------
// Geo math
// ---------------------------------------------------------------------------

const DEG_TO_RAD = Math.PI / 180;
/** Earth radius in nautical miles. */
const EARTH_RADIUS_NM = 3440.065;

/** Haversine distance between two lat/lng points, in nautical miles. */
function haversineNm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const dLat = (lat2 - lat1) * DEG_TO_RAD;
  const dLng = (lng2 - lng1) * DEG_TO_RAD;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG_TO_RAD) *
      Math.cos(lat2 * DEG_TO_RAD) *
      Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_NM * Math.asin(Math.sqrt(a));
}

// ---------------------------------------------------------------------------
// Core detection
// ---------------------------------------------------------------------------

/**
 * Extract MMSI from a vessel event. Returns null for non-vessel events.
 */
function extractMMSI(event: NormalizedEvent): number | null {
  const raw = event.raw as Record<string, unknown> | undefined;
  if (!raw) return null;
  const mmsi = raw.mmsi;
  if (typeof mmsi === 'number' && mmsi > 0) return mmsi;
  if (typeof mmsi === 'string') {
    const parsed = parseInt(mmsi, 10);
    if (parsed > 0) return parsed;
  }
  return null;
}

/**
 * Extract speed over ground (knots) from raw vessel data.
 */
function extractSpeed(event: NormalizedEvent): number {
  const raw = event.raw as Record<string, unknown> | undefined;
  if (!raw) return 0;
  const speed = raw.speed ?? raw.sog;
  return typeof speed === 'number' ? speed : 0;
}

/**
 * Detect AIS anomalies from current vessel position events.
 * Call this each analysis tick with the full event set.
 */
export function detectAISSpoofing(events: NormalizedEvent[]): AISAnomaly[] {
  const now = Date.now();
  const anomalies: AISAnomaly[] = [];

  // Filter to vessel position events only
  const vesselEvents = events.filter(
    (e) =>
      e.type === 'vessel_position' &&
      e.location != null &&
      isFinite(e.location.lat) &&
      isFinite(e.location.lng),
  );

  // Track which vessels we've seen this tick (for dark zone detection)
  const seenMMSIs = new Set<number>();

  for (const ev of vesselEvents) {
    const mmsi = extractMMSI(ev);
    if (mmsi === null) continue;

    seenMMSIs.add(mmsi);

    // Vessel reappeared — clear dark-zone and dark-ship flags
    darkZoneFlagged.delete(mmsi);
    darkShipFlagged.delete(mmsi);

    const lat = ev.location!.lat;
    const lng = ev.location!.lng;
    const speed = extractSpeed(ev);
    const tsMs = getEventTimestamp(ev);
    const vesselName = ev.title || `MMSI ${mmsi}`;

    // --- Check 1: Impossible speed ---
    if (speed > MAX_SPEED_KTS) {
      anomalies.push({
        mmsi,
        vesselName,
        anomalyType: 'impossible-speed',
        description: `${vesselName} reporting ${speed.toFixed(1)} kts (max plausible: ${MAX_SPEED_KTS} kts)`,
        lat,
        lng,
        detectedAt: new Date(now).toISOString(),
        reportedSpeed: speed,
      });
    }

    // --- Check 2: Position jump ---
    const prev = vesselHistory.get(mmsi);
    if (prev) {
      const timeDeltaMs = tsMs - prev.timestampMs;

      // Only check jumps within a 5-minute window (avoid stale comparisons)
      if (timeDeltaMs > 0 && timeDeltaMs <= FIVE_MINUTES_MS) {
        const distNm = haversineNm(prev.lat, prev.lng, lat, lng);

        if (distNm > MAX_DISTANCE_NM_5MIN) {
          const timeDeltaSec = Math.round(timeDeltaMs / 1000);
          anomalies.push({
            mmsi,
            vesselName,
            anomalyType: 'position-jump',
            description:
              `${vesselName} jumped ${distNm.toFixed(1)} nm in ${timeDeltaSec}s ` +
              `(${prev.lat.toFixed(2)},${prev.lng.toFixed(2)} → ${lat.toFixed(2)},${lng.toFixed(2)})`,
            lat,
            lng,
            detectedAt: new Date(now).toISOString(),
            jumpDistanceNm: Math.round(distNm * 10) / 10,
            timeDeltaSec,
          });
        }
      }
    }

    // Update history with latest position
    vesselHistory.set(mmsi, {
      lat,
      lng,
      timestampMs: tsMs,
      speed,
      name: vesselName,
    });
  }

  // --- Check 3: Dark zone silence ---
  for (const [mmsi, pos] of vesselHistory) {
    if (seenMMSIs.has(mmsi)) continue; // Still reporting — not dark
    if (darkZoneFlagged.has(mmsi)) continue; // Already alerted for this dark period

    const silenceMs = now - pos.timestampMs;
    const silenceSec = silenceMs / 1000;

    if (silenceSec < DARK_ZONE_SILENCE_SEC) continue;

    // Check if last known position was in a dark zone
    for (const zone of DARK_ZONES) {
      const distNm = haversineNm(pos.lat, pos.lng, zone.lat, zone.lng);
      if (distNm <= zone.radiusNm) {
        darkZoneFlagged.add(mmsi); // Prevent duplicate alerts
        anomalies.push({
          mmsi,
          vesselName: pos.name,
          anomalyType: 'dark-zone',
          description:
            `${pos.name} went dark in ${zone.name} — no report for ` +
            `${Math.round(silenceSec / 60)} minutes`,
          lat: pos.lat,
          lng: pos.lng,
          detectedAt: new Date(now).toISOString(),
          silenceDurationSec: Math.round(silenceSec),
        });
        break; // One dark-zone alert per vessel
      }
    }
  }

  // --- Check 4: Dark ship — vessel silent 30+ min in any major shipping lane ---
  for (const [mmsi, pos] of vesselHistory) {
    if (seenMMSIs.has(mmsi)) continue;
    if (darkShipFlagged.has(mmsi)) continue;
    if (darkZoneFlagged.has(mmsi)) continue; // Already covered by dark-zone check

    const silenceMs = now - pos.timestampMs;
    const silenceSec = silenceMs / 1000;

    if (silenceSec < DARK_ZONE_SILENCE_SEC) continue;

    for (const lane of SHIPPING_LANES) {
      const distNm = haversineNm(pos.lat, pos.lng, lane.lat, lane.lng);
      if (distNm <= lane.radiusNm) {
        darkShipFlagged.add(mmsi);
        anomalies.push({
          mmsi,
          vesselName: pos.name,
          anomalyType: 'dark-ship',
          description:
            `${pos.name} went dark in ${lane.name} — no AIS report for ` +
            `${Math.round(silenceSec / 60)} minutes`,
          lat: pos.lat,
          lng: pos.lng,
          detectedAt: new Date(now).toISOString(),
          silenceDurationSec: Math.round(silenceSec),
        });
        break;
      }
    }
  }

  // Prune stale history entries
  pruneHistory(now);

  return anomalies;
}

/**
 * Remove position history entries older than the TTL and cap size.
 */
function pruneHistory(now: number): void {
  const cutoff = now - HISTORY_TTL_MS;

  for (const [mmsi, pos] of vesselHistory) {
    if (pos.timestampMs < cutoff) {
      vesselHistory.delete(mmsi);
    }
  }

  // If still too many, trim oldest entries
  if (vesselHistory.size > MAX_TRACKED_VESSELS) {
    const entries = Array.from(vesselHistory.entries())
      .sort((a, b) => a[1].timestampMs - b[1].timestampMs);
    const excess = entries.length - MAX_TRACKED_VESSELS;
    for (let i = 0; i < excess; i++) {
      vesselHistory.delete(entries[i][0]);
    }
  }
}

/** Reset all state (for testing). */
export function resetAISSpoofingState(): void {
  vesselHistory.clear();
  darkZoneFlagged.clear();
  darkShipFlagged.clear();
}
